import { Injectable } from '@angular/core';

import { BehaviorSubject, from, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import Geometry from "@arcgis/core/geometry/Geometry";
import QueryTask from "@arcgis/core/tasks/QueryTask";
import Query from "@arcgis/core/rest/support/Query";
import Graphic from '@arcgis/core/Graphic';
import request from '@arcgis/core/request';

import { ConfigService, MapService } from "../../../shared";
import { Dibujo, DibujoType, geometryTypeToDibujoType } from "../../../chacra-core";
import { DIBUJOS_SYMBOLS } from "../constants";

import * as shpwrite from 'shp-write';
import { saveAs } from 'file-saver';

const esriRequest = request;
import RequestResponse = __esri.RequestResponse;
import Polygon from "@arcgis/core/geometry/Polygon";
import Polyline from "@arcgis/core/geometry/Polyline";
import Point from "@arcgis/core/geometry/Point";
import {FeatureItem, FeatureLayerProperties, MapGeoProcessJob, MapGeoProcessJobResult} from '../models';


@Injectable({
  providedIn: 'root'
})
export class FeaturesMapService extends MapService {
  sketchAction$ = new BehaviorSubject<any>(null);

  featureItemToDibujo(graphicAttr: string, attrId: string, item: any): Dibujo {
    const geometry = this.stringJsonToGeometry(item[graphicAttr], false);

    const dibujoTipo = 'paths' in geometry ? DibujoType.POLYLINE : DibujoType.POLYGON;
    return {
      dibujoId: Number(item[attrId]),
      dibujoGeometria: JSON.stringify(geometry),
      dibujoTipo: dibujoTipo
    }
  }

  dibujoToGraphic(dibujo: Dibujo, attributes = {}): Graphic[] {
    const geometry = this.stringJsonToGeometry(dibujo.dibujoGeometria, false);
    let symbol: any = DIBUJOS_SYMBOLS.line;
    if (dibujo.dibujoTipo === DibujoType.POLYGON) {
      symbol = DIBUJOS_SYMBOLS.fill;
    }
    if (dibujo.dibujoTipo === DibujoType.POINT) {
      symbol = DIBUJOS_SYMBOLS.marker;
    }
    const graphic = new Graphic({
      geometry,
      attributes: {
        ...attributes,
        dibujoId: dibujo.dibujoId,
        OBJECTID: this.nextId()
      },
      symbol,
    });
    const label = this.createGraphicText(graphic.geometry, dibujo.dibujoId, dibujo.dibujoTipo);
    return [graphic, label];
  }

  importDibujos(name: string, file: any, wkid: number): Observable<any> {
    const params = {
      name,
      targetSR: {
        wkid
      },
      maxRecordCount: 1000,
      enforceInputFileSizeLimit: true,
      enforceOutputJsonSizeLimit: true
    };

    const content = {
      filetype: 'shapefile',
      publishParameters: JSON.stringify(params),
      f: 'json'
    };

    const form = new FormData();
    form.append('file', file, name);

    return from(esriRequest(
      'https://www.arcgis.com/sharing/rest/content/features/generate',
      {
        method: 'post',
        query: content,
        body: form,
        responseType: 'json'
      }
    )
    ).pipe(
      map((response: RequestResponse) => response.data.featureCollection.layers),
      map(layers => layers.reduce(
        (graphics: Graphic[], layer: any) =>
          graphics.concat(
            layer.featureSet.features.filter(
              (feature: Graphic) =>
                'paths' in feature.geometry ||
                'rings' in feature.geometry
            )
              .map((feature: Graphic) => {
                const dibujoTipo = 'paths' in feature.geometry ? DibujoType.POLYLINE : DibujoType.POLYGON;
                const geometry = feature.geometry;
                return {
                  dibujoId: this.nextId(),
                  dibujoGeometria: JSON.stringify(geometry),
                  dibujoTipo: dibujoTipo
                }
              })
          ),
        []
      )
      ),
      catchError(error => {
        return throwError(`Error al importar el Shapefile: ${error.message}`);
      })
    );
  }

  /** Exportar los dibujos a un fichero shapefile */
  exportDibujos(folder: string, features: any[]) {

    return new Observable((observer) => {
      try {
        // (optional) set names for feature types and zipped folder
        const options = {
          folder,
          types: {
            point: 'points',
            polygon: 'polygons',
            line: 'lines'
          }
        };
        // a GeoJSON bridge for features
        shpwrite.download({
          type: 'FeatureCollection',
          features: features
        }, options);
        observer.next();
        observer.complete();
      } catch (e) {
        observer.error(e);
      }
    });
  }

  getGeometricExportFromType(graphic: any){
    const geometry = graphic.geometry.toJSON();

    let coordinates = [];
    switch (graphic.geometry.type) {
      case DibujoType.POLYGON: case DibujoType.POLYLINE:
        coordinates = geometry.rings;
        break;

      case DibujoType.POINT:
        coordinates = [graphic.geometry.x, graphic.geometry.y];
        break;
    }

    // Poner la primera letra en mayuscula, para que lo acepte el shp-write
    let geometryType: string = graphic.geometry.type;
    geometryType = geometryType[0].toUpperCase() + geometryType.slice(1);

    return {
      type: geometryType,
      coordinates
    };
  }

  searchPadrones(departamento: string, padron: string, spatialReference: any) {

    if (departamento && padron) {
      const qt = new QueryTask({
        url: ConfigService.config.urlLayers.padrones
      });
      const q = new Query({
        returnGeometry: true,
        outFields: ['*'],
        where: `DEPTO='${departamento}' AND PADRON=${padron}`,
        outSpatialReference: spatialReference
      });

      return qt.execute(q);
    }
  }

  private createGraphicText(geometry: Geometry, id: number, type: DibujoType): Graphic {
    const symbol = {
      ...DIBUJOS_SYMBOLS.text,
      text: id.toString()
    };
    let geometryLabelPoint;
    if (type === DibujoType.POINT) {
      geometryLabelPoint = geometry;
    } else {
      geometryLabelPoint = this.geometryLabelPoint(geometry);
    }
    return new Graphic({
      geometry: geometryLabelPoint,
      symbol
    });
  }

  getGeometryType(geometry: Geometry): DibujoType {
    let type = DibujoType.POINT;
    if (geometry.hasOwnProperty('rings')) {
      type = DibujoType.POLYGON;
    } else if (geometry.hasOwnProperty('paths')) {
      type = DibujoType.POLYLINE;
    }
    return type;
  }

  selectFeaturesIntersect(layerView, geometry): Promise<Graphic[]> {
    return new Promise<Graphic[]>((resolve, reject) => {
      if (layerView) {
        // create a query and set its geometry parameter to the
        // rectangle that was drawn on the view
        const query = {
          geometry: geometry,
          returnGeometry: true,
          outFields: ["*"]
        };

        // query graphics from the layer view. Geometry set for the query
        // can be polygon for point features and only intersecting geometries are returned
        layerView.queryFeatures(query)
          .then((results) => {
            resolve(results.features);
          });
      }
    });
  }

  createHole(containerDibujo: Dibujo, holeDibujo: Dibujo) {

    let dibujoResult = null;
    const geometry1 = this.stringJsonToGeometry(containerDibujo.dibujoGeometria);
    const geometry2 = this.stringJsonToGeometry(holeDibujo.dibujoGeometria);
    const intersects = this.intersects(geometry2, geometry1);

    if (intersects) {
      let diffGeometry: Geometry;
      const diffReturn = this.difference(geometry1, geometry2);
      if (diffReturn instanceof Geometry) {
        diffGeometry = diffReturn;
      } else {
        diffGeometry = diffReturn[0];
      }

      dibujoResult = {
        dibujoId: this.nextId(),
        dibujoGeometria: JSON.stringify(diffGeometry.toJSON()),
        dibujoTipo: geometryTypeToDibujoType(diffGeometry.type)
      };
    }
    return dibujoResult;
  }

  launchSketchAction(action: any): void {
    this.sketchAction$.next(action);
  }

  watchSketchAction(): Observable<any> {
    return this.sketchAction$;
  }

  combineFeatureLayer(configBase: FeatureLayerProperties, config: FeatureLayerProperties) {
    const fields = [...configBase.fields, ...config.fields];
    const labelingInfo = {...configBase.labelingInfo, ...config.labelingInfo};

    return {
      ...configBase,
      ...config,
      fields,
      labelingInfo,
    };
  }

  showDataLikeFields(jobType: string, data: any) {
    const job: MapGeoProcessJob = ConfigService.getMapConfig().jobs.find(j => j.slug === jobType);
    let trs = Object.keys(data).map(k => {
      let label = k;
      if(job && job.resultLabels){
        const jobLabel = job.resultLabels.find(jl => jl.key === k);
        if(jobLabel)
          label = jobLabel.label;
      }
      return `
        <tr>
          <th class="esri-feature-fields__field-header">${label}</th>
          <td class="esri-feature-fields__field-data">${data[k]}</td>
        </tr>
      `;
    });
    return `
      <table class="esri-widget__table">
        <tbody>
          ${trs.join('')}
        </tbody>
      </table>
    `;
  }

  JSONToCSV(data: any, excludes: string[] = []) {
    const items = Object.keys(data).filter(k => !excludes.includes(k)).map(k => [k, data[k]]);
    const header = ['Keys', 'Values'];
    const csv = [
      header.join(','), // header row first
      ...items.map(it => it.join(','))
    ].join('\r\n');

    const blob = new Blob([csv], {type: 'text/csv' });
    saveAs(blob, "export.csv");
  }

}
